/**
 * Parses email addresses from various formats, including name and email combinations.
 *
 * Handles multiple common email formats including plain emails and name/email combinations
 * with angular brackets. This is a simplified parser that handles real-world usage patterns
 * rather than strict RFC compliance.
 *
 * Supported formats:
 * - x@example.com
 * - "first last" <x@example.com>
 * - 'first last' <x@example.com>
 * - first last <x@example.com>
 *
 * @param text - The text containing email address to parse
 * @returns Object with email and optional name properties
 *
 * @example
 * ```typescript
 * getEmailHandleFromAngularBrackets('john@example.com')
 * // returns { email: 'john@example.com' }
 *
 * getEmailHandleFromAngularBrackets('"John Doe" <john@example.com>')
 * // returns { email: 'john@example.com', name: 'John Doe' }
 *
 * getEmailHandleFromAngularBrackets('John Doe <john@example.com>')
 * // returns { email: 'john@example.com', name: 'John Doe' }
 * ```
 */
export function getEmailHandleFromAngularBrackets(text?: string | null): {
  name?: string
  email: string
} {
  // non-rfc5322 compliant parsing
  // a compromise between:
  // - out-of-date rfc
  // - 8kb bloated parser https://github.com/jackbearheart/email-addresses
  // - real usage (gmail, mail.app)
  //
  // handles:
  // x@example.com
  // "first last" <x@example.com>
  // 'first last' <x@example.com>
  //  first   last  <x@example.com>
  //
  //  if it can't parse (i.e. email is invalid) - it returns trimmed text
  //
  if (!text) return { email: '' }
  const match = text.match(/^\s*([^>]+)?<([^>]+)>\s*$/)
  if (match) {
    const emailMatch = match[2]
    const nameMatch = match[1]
    let name = ''
    if (nameMatch) {
      name = nameMatch
        .replace(/([ \t]|\r\n)+/g, ' ')
        .replace(/^\s*/, '')
        .replace(/\s*$/, '')
        .replace(/^["'“”‘’]/g, '')
        .replace(/["'“”‘’]$/g, '')
    }
    const email = emailMatch?.trim() || ''
    if (name) {
      return { email, name }
    } else {
      return { email }
    }
  } else {
    return { email: text.trim() }
  }
}
